import { DateToUnixConverter, UnixToDateConverter } from "common/DateConverter";
import { dataInterval } from "common/types";
import { t } from "i18next";
import moment from "moment";
import i18n from "../../common/languageHandling/i18n";
import {
  IDateHandlerObject,
  IUnixTimeObject,
  PeriodType,
} from "../../nodeDetails/types";

const shortMonthNames = [
  i18n.t("Jan"),
  i18n.t("Feb"),
  i18n.t("Mars"),
  i18n.t("Apr"),
  i18n.t("May"),
  i18n.t("June"),
  i18n.t("July"),
  i18n.t("Aug"),
  i18n.t("Sep"),
  i18n.t("Oct"),
  i18n.t("Nov"),
  i18n.t("Dec"),
];
export const longMonthNames = [
  i18n.t("January"),
  i18n.t("February"),
  i18n.t("March"),
  i18n.t("April"),
  i18n.t("May"),
  i18n.t("June"),
  i18n.t("July"),
  i18n.t("August"),
  i18n.t("September"),
  i18n.t("October"),
  i18n.t("November"),
  i18n.t("December"),
];

export const dateOneYearPrior = (date: number) => {
  return DateToUnixConverter(moment.unix(date).subtract(1, "year"));
};
export function getPeriodType(date: string) {
  let datePeriod: PeriodType = "month";
  switch (date) {
    case t("Week"):
      datePeriod = "week";
      break;
    case t("Month"):
      datePeriod = "month";
      break;
    case t("Year"):
      datePeriod = "year";
      break;
    case t("Day"):
      datePeriod = "days";
      break;
    default:
      datePeriod = "month";
  }
  return datePeriod;
}

export const getOneIntervalLater = (date: number, periodType: PeriodType) => {
  switch (periodType) {
    case "days":
      return moment.unix(date).add(1, "day").unix();
    case "month":
      return moment.unix(date).add(1, "month").unix();
    case "week":
      return moment.unix(date).add(1, "week").unix();
    case "year":
      return moment.unix(date).add(1, "year").unix();
    default:
      return moment.unix(date).endOf(periodType).unix();
  }
};

export const correctTimeSlot = (
  occupationDate: number,
  requestedFromDate: number,
  interval: dataInterval
) => {
  if (occupationDate > requestedFromDate) {
    if (interval === "m") {
      const convertedOccDate = UnixToDateConverter(occupationDate);
      const firstWholeMonth = convertedOccDate
        .add(1, "month")
        .startOf("month")
        .unix();
      return firstWholeMonth;
    }
    return occupationDate;
  }
  return requestedFromDate;
};

export const handlePreviousDateSelection = (
  dateHandlerObj: IDateHandlerObject
) => {
  const period = getPeriodType(dateHandlerObj.period);
  const fromDate = moment(dateHandlerObj.from).subtract(1, period);

  if (period === "year") {
    return fromDate.year();
  }
  if (period === "month") {
    return fromDate.year() + " " + shortMonthNames[fromDate.month()];
  }
  if (period === "week") {
    const to = moment(dateHandlerObj.to).subtract(7, "days");
    return (
      shortMonthNames[fromDate.month()] +
      " " +
      fromDate.date() +
      " - " +
      shortMonthNames[to.month()] +
      " " +
      to.date()
    );
  }
  if (period === "days") {
    return shortMonthNames[fromDate.month()] + " " + fromDate.date();
  }
};
export const newHandlePreviousDateSelection = (
  dateHandlerObj: IDateHandlerObject
) => {
  const period = getPeriodType(dateHandlerObj.period);
  const fromDate = moment(dateHandlerObj.from).subtract(1, period);

  if (period === "year") {
    return fromDate.format("YYYY");
  }
  if (period === "month") {
    return shortMonthNames[fromDate.month()];
  }
  if (period === "week") {
    const to = moment(dateHandlerObj.to).subtract(7, "days");
    return (
      shortMonthNames[fromDate.month()] +
      " " +
      fromDate.date() +
      " - " +
      shortMonthNames[to.month()] +
      " " +
      to.date()
    );
  }
  if (period === "days") {
    return shortMonthNames[fromDate.month()] + " " + fromDate.date();
  }
};

export const handleCurrentDate = (dateHandlerObj: IDateHandlerObject) => {
  const period = getPeriodType(dateHandlerObj.period);

  if (period === "year") {
    return dateHandlerObj.from.format("YYYY");
  }
  if (period === "month") {
    return (
      dateHandlerObj.from.format("YYYY") +
      " " +
      shortMonthNames[dateHandlerObj.from.month()]
    );
  }
  if (period === "week") {
    return (
      shortMonthNames[dateHandlerObj.from.month()] +
      " " +
      dateHandlerObj.from.date() +
      " - " +
      shortMonthNames[dateHandlerObj.to.month()] +
      " " +
      dateHandlerObj.to.date()
    );
  }
  if (period === "days") {
    return (
      shortMonthNames[dateHandlerObj.from.month()] +
      " " +
      dateHandlerObj.from.date()
    );
  }
};

export const newHandleCurrentDate = (dateHandlerObj: IDateHandlerObject) => {
  const period = getPeriodType(dateHandlerObj.period);

  if (period === "year") {
    return dateHandlerObj.from.year();
  }
  if (period === "month") {
    return shortMonthNames[dateHandlerObj.from.month()];
  }
  if (period === "week") {
    return (
      shortMonthNames[dateHandlerObj.from.month()] +
      " " +
      dateHandlerObj.from.date() +
      " - " +
      shortMonthNames[dateHandlerObj.to.month()] +
      " " +
      dateHandlerObj.to.date()
    );
  }
  if (period === "days") {
    return (
      shortMonthNames[dateHandlerObj.from.month()] +
      " " +
      dateHandlerObj.from.date()
    );
  }
};

export const handleNextDateSelection = (dateHandlerObj: IDateHandlerObject) => {
  const period = getPeriodType(dateHandlerObj.period);
  const fromDate = moment(dateHandlerObj.from).add(1, period);
  if (period === "year") {
    return fromDate.format("YYYY");
  }
  if (period === "month") {
    return fromDate.format("YYYY") + " " + shortMonthNames[fromDate.month()];
  }
  if (period === "week") {
    const toDate = moment(dateHandlerObj.to).add(7, "days");
    return (
      shortMonthNames[fromDate.month()] +
      " " +
      fromDate.date() +
      " - " +
      shortMonthNames[toDate.month()] +
      " " +
      toDate.date()
    );
  }
  if (period === "days") {
    return shortMonthNames[fromDate.month()] + " " + fromDate.date();
  }
};

export const newHandleNextDateSelection = (
  dateHandlerObj: IDateHandlerObject
) => {
  const period = getPeriodType(dateHandlerObj.period);
  const fromDate = moment(dateHandlerObj.from).add(1, period);
  if (period === "year") {
    return fromDate.format("YYYY");
  }
  if (period === "month") {
    return shortMonthNames[fromDate.month()];
  }
  if (period === "week") {
    const to = moment(dateHandlerObj.to).add(7, "days");
    return (
      shortMonthNames[fromDate.month()] +
      " " +
      fromDate.date() +
      " - " +
      shortMonthNames[to.month()] +
      " " +
      to.date()
    );
  }
  if (period === "days") {
    return shortMonthNames[fromDate.month()] + " " + fromDate.date();
  }
};

export const currentChosenPeriod = (dateHandlerObj: IDateHandlerObject) => {
  if (dateHandlerObj.period === "Year") {
    const obj: IDateHandlerObject = {
      period: dateHandlerObj.period,
      from: moment(dateHandlerObj.from).startOf("year"),
      to: moment(dateHandlerObj.from).endOf("year"),
    };
    return obj;
  }
  if (dateHandlerObj.period === "Month") {
    const obj: IDateHandlerObject = {
      period: dateHandlerObj.period,
      from: moment(dateHandlerObj.from).startOf("month"),
      to: moment(dateHandlerObj.from).endOf("month"),
    };
    return obj;
  }
  if (dateHandlerObj.period === "Week") {
    const obj: IDateHandlerObject = {
      period: dateHandlerObj.period,
      from: moment(dateHandlerObj.from).startOf("week"),
      to: moment(dateHandlerObj.from).endOf("week"),
    };
    return obj;
  }
  if (dateHandlerObj.period === "Day") {
    const obj: IDateHandlerObject = {
      period: dateHandlerObj.period,
      from: moment(dateHandlerObj.from).startOf("day"),
      to: moment(dateHandlerObj.from).endOf("day"),
    };
    return obj;
  } else {
    const obj: IDateHandlerObject = {
      period: dateHandlerObj.period,
      from: moment(dateHandlerObj.from).startOf("month"),
      to: moment(dateHandlerObj.from).endOf("month"),
    };
    return obj;
  }
};
export const getDaysInCurrentMonth = () => {
  return moment().daysInMonth();
};

export const getCurrentMonthUnixTimeObject = () => {
  const date = moment();
  const period: IUnixTimeObject = {
    period: "month",
    from:
      date.date() === 1
        ? DateToUnixConverter(date.subtract(1, "month").startOf("month"))
        : DateToUnixConverter(date.startOf("month")),
    to: DateToUnixConverter(date.endOf("month")),
  };
  return period;
};

export const getAvailablePeriods = (canViewHours: boolean) => {
  if (canViewHours) return [t("Month"), t("Year"), t("Week"), t("Day")];

  return [t("Month"), t("Year"), t("Week")];
};
export const getPreviousFromDate = (dataHandlerObject: IDateHandlerObject) => {
  const datePeriod = getPeriodType(dataHandlerObject.period);

  return +moment(dataHandlerObject.from)
    .subtract(1, datePeriod)
    .endOf(datePeriod)
    .unix();
};
